/**
 * Smart Prefetch Strategy
 * Predictive prefetching based on user behavior and navigation patterns
 * @version 1.0.0
 */

class SmartPrefetch {
  constructor() {
    this.networkSpeed = this.detectNetworkSpeed();
    this.saveData = this.detectSaveData();
    this.prefetchedUrls = new Set();
    this.navigationHistory = [];
    this.hoverIntent = new Map();
    this.viewportLinks = new Set();
    this.config = {
      enabled: !this.saveData && this.networkSpeed !== 'slow',
      hoverDelay: 100, // ms before prefetch on hover
      viewportMargin: '200px',
      maxPrefetch: 10,
      maxHistorySize: 20
    };
    this.init();
  }

  init() {
    if (!this.config.enabled) {
      console.log('⏸️ Smart Prefetch disabled (Save-Data or slow connection)');
      return;
    }

    // Monitor network changes
    if ('connection' in navigator) {
      navigator.connection.addEventListener('change', () => {
        this.networkSpeed = this.detectNetworkSpeed();
        this.saveData = this.detectSaveData();
        this.config.enabled = !this.saveData && this.networkSpeed !== 'slow';

        if (!this.config.enabled) {
          console.log('⏸️ Smart Prefetch disabled due to network change');
        }
      });
    }

    // Setup prefetch strategies
    this.setupHoverPrefetch();
    this.setupViewportPrefetch();
    this.setupNavigationTracking();
    this.prefetchLikelyDestinations();
  }

  /**
   * Detect network speed
   */
  detectNetworkSpeed() {
    if (!('connection' in navigator)) return 'fast';

    const conn = navigator.connection;
    const effectiveType = conn.effectiveType;

    const speedMap = {
      'slow-2g': 'slow',
      '2g': 'slow',
      '3g': 'medium',
      '4g': 'fast'
    };

    return speedMap[effectiveType] || 'fast';
  }

  /**
   * Detect Save-Data mode
   */
  detectSaveData() {
    if ('connection' in navigator && 'saveData' in navigator.connection) {
      return navigator.connection.saveData;
    }
    return false;
  }

  /**
   * Setup hover-based prefetching
   */
  setupHoverPrefetch() {
    document.addEventListener('mouseover', (e) => {
      if (!this.config.enabled) return;

      const link = e.target.closest('a[href]');
      if (!link) return;

      const url = this.normalizeUrl(link.href);
      if (!this.shouldPrefetch(url)) return;

      // Track hover intent
      const hoverStart = Date.now();
      this.hoverIntent.set(link, hoverStart);

      // Prefetch after delay (shows user intent)
      setTimeout(() => {
        const stillHovering = this.hoverIntent.get(link) === hoverStart;
        if (stillHovering) {
          this.prefetch(url, 'hover');
        }
      }, this.config.hoverDelay);
    }, { passive: true });

    document.addEventListener('mouseout', (e) => {
      const link = e.target.closest('a[href]');
      if (link) {
        this.hoverIntent.delete(link);
      }
    }, { passive: true });
  }

  /**
   * Setup viewport-based prefetching (visible links)
   */
  setupViewportPrefetch() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const link = entry.target;
        const url = this.normalizeUrl(link.href);

        if (entry.isIntersecting) {
          this.viewportLinks.add(url);

          // Prefetch high-priority viewport links
          if (this.isPriorityLink(link)) {
            setTimeout(() => {
              if (this.viewportLinks.has(url)) {
                this.prefetch(url, 'viewport-priority');
              }
            }, 1000); // Wait 1s to ensure user is interested
          }
        } else {
          this.viewportLinks.delete(url);
        }
      });
    }, {
      rootMargin: this.config.viewportMargin
    });

    // Observe all internal links
    document.querySelectorAll('a[href]').forEach(link => {
      if (this.isInternalUrl(link.href)) {
        observer.observe(link);
      }
    });
  }

  /**
   * Track navigation patterns
   */
  setupNavigationTracking() {
    // Track current page
    this.trackNavigation(window.location.href);

    // Track clicks
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href]');
      if (link && this.isInternalUrl(link.href)) {
        this.trackNavigation(link.href);
      }
    });
  }

  /**
   * Track navigation to build pattern history
   */
  trackNavigation(url) {
    const normalized = this.normalizeUrl(url);
    this.navigationHistory.push({
      url: normalized,
      timestamp: Date.now()
    });

    // Keep history limited
    if (this.navigationHistory.length > this.config.maxHistorySize) {
      this.navigationHistory.shift();
    }

    // Store in localStorage for cross-session patterns
    try {
      const stored = JSON.parse(localStorage.getItem('prefetch_history') || '[]');
      stored.push(normalized);
      if (stored.length > this.config.maxHistorySize) stored.shift();
      localStorage.setItem('prefetch_history', JSON.stringify(stored));
    } catch (e) {
      // Ignore storage errors
    }
  }

  /**
   * Prefetch likely destinations based on patterns
   */
  prefetchLikelyDestinations() {
    if (!this.config.enabled) return;

    // Wait for page to settle
    setTimeout(() => {
      // Get navigation patterns
      const patterns = this.analyzeNavigationPatterns();

      // Prefetch top predictions
      patterns.slice(0, 3).forEach(url => {
        this.prefetch(url, 'predicted');
      });
    }, 2000); // Wait 2s after page load
  }

  /**
   * Analyze navigation patterns to predict next destinations
   */
  analyzeNavigationPatterns() {
    const predictions = new Map();

    // Get stored history
    let history = [];
    try {
      history = JSON.parse(localStorage.getItem('prefetch_history') || '[]');
    } catch (e) {
      history = [];
    }

    // Combine with current session
    const allHistory = [...history, ...this.navigationHistory.map(h => h.url)];

    // Find common patterns (what comes after current page)
    const currentUrl = this.normalizeUrl(window.location.href);
    let foundCurrent = false;

    allHistory.forEach((url, index) => {
      if (foundCurrent && index < allHistory.length - 1) {
        const nextUrl = allHistory[index + 1];
        predictions.set(nextUrl, (predictions.get(nextUrl) || 0) + 1);
      }
      if (url === currentUrl) {
        foundCurrent = true;
      }
    });

    // Sort by frequency
    return Array.from(predictions.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([url]) => url);
  }

  /**
   * Check if link is high priority
   */
  isPriorityLink(link) {
    // Check for priority indicators
    const priorityClasses = ['btn', 'button', 'cta', 'primary', 'shop-now', 'add-to-cart'];
    const classList = link.className.toLowerCase();

    return priorityClasses.some(cls => classList.includes(cls));
  }

  /**
   * Check if URL should be prefetched
   */
  shouldPrefetch(url) {
    if (!this.config.enabled) return false;
    if (this.prefetchedUrls.has(url)) return false;
    if (this.prefetchedUrls.size >= this.config.maxPrefetch) return false;
    if (!this.isInternalUrl(url)) return false;
    if (url === window.location.href) return false;

    // Don't prefetch cart, checkout, account pages (dynamic content)
    const excludePatterns = ['/cart', '/checkout', '/account', '/admin'];
    if (excludePatterns.some(pattern => url.includes(pattern))) return false;

    return true;
  }

  /**
   * Normalize URL for comparison
   */
  normalizeUrl(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.origin + parsed.pathname;
    } catch (e) {
      return url;
    }
  }

  /**
   * Check if URL is internal
   */
  isInternalUrl(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.origin === window.location.origin;
    } catch (e) {
      return false;
    }
  }

  /**
   * Prefetch URL
   */
  prefetch(url, reason = 'unknown') {
    if (!this.shouldPrefetch(url)) return;

    // Use multiple prefetch methods for best coverage
    this.prefetchedUrls.add(url);

    // Method 1: Link prefetch (best browser support)
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    link.as = 'document';
    document.head.appendChild(link);

    // Method 2: Fetch API (more control, modern browsers)
    if ('fetch' in window && this.networkSpeed === 'fast') {
      fetch(url, {
        method: 'GET',
        priority: 'low',
        credentials: 'same-origin'
      }).catch(() => {
        // Ignore prefetch errors
      });
    }

    console.log(`⚡ Prefetching (${reason}):`, url);
  }

  /**
   * Prefetch specific URL manually
   */
  prefetchUrl(url) {
    const normalized = this.normalizeUrl(url);
    this.prefetch(normalized, 'manual');
  }

  /**
   * Get status
   */
  getStatus() {
    return {
      enabled: this.config.enabled,
      network: this.networkSpeed,
      saveData: this.saveData,
      prefetched: this.prefetchedUrls.size,
      historySize: this.navigationHistory.length,
      viewportLinks: this.viewportLinks.size,
      predictions: this.analyzeNavigationPatterns().slice(0, 5)
    };
  }

  /**
   * Clear all prefetch data
   */
  clear() {
    this.prefetchedUrls.clear();
    this.navigationHistory = [];
    this.viewportLinks.clear();

    try {
      localStorage.removeItem('prefetch_history');
    } catch (e) {
      // Ignore
    }

    console.log('🧹 Prefetch data cleared');
  }
}

// Initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.smartPrefetch = new SmartPrefetch();
    console.log('🎯 Smart Prefetch initialized:', window.smartPrefetch.getStatus());
  });
} else {
  window.smartPrefetch = new SmartPrefetch();
  console.log('🎯 Smart Prefetch initialized:', window.smartPrefetch.getStatus());
}

// Export
if (typeof module !== 'undefined' && module.exports) {
  module.exports = SmartPrefetch;
}
